/**
 * V2 Design Tokens — extracted from wine_services_proto_V6_1429
 *
 * Single source of truth for all v2 visual properties.
 * Used both as CSS custom properties (injected via ThemeProvider)
 * and as a TS object for styled-components interpolation.
 */

export const colors = {
  terracotta: '#e25d34',
  terracottaDark: '#c84b1f',
  terracottaSoft: '#f4ddd2',
  ink: '#2b3d54',
  inkMuted: '#5c5751',
  inkFaint: '#8b857d',
  paper: '#eff0f2',
  paperWarm: '#f1ebe1',
  line: '#e3dccf',
  lineSoft: '#ede7db',
  olive: '#4a6b3a',
  oliveSoft: '#e4ebde',
  amber: '#b8862a',
  amberSoft: '#f3e7c8',
  crimson: '#9c2a1a',
  crimsonSoft: '#f0d5cf',
  indigo: '#3a4a6b',
  indigoSoft: '#dde3ed',
  white: '#fffdf9'
} as const;

export const shadows = {
  sm: '0 1px 2px rgba(43, 61, 84, 0.04)',
  md: '0 4px 16px rgba(43, 61, 84, 0.06)',
  lg: '0 20px 60px rgba(43, 61, 84, 0.12)',
  float: '0 8px 32px rgba(43, 61, 84, 0.10), 0 2px 6px rgba(43, 61, 84, 0.06)'
} as const;

export const fonts = {
  serif: '"SilkSerifRegular", Georgia, serif',
  sans: '"SFCompactText", -apple-system, BlinkMacSystemFont, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", monospace',
  heading: '"Oswald", sans-serif',
  headingSemibold: '"OswaldSemiBold", "Oswald", sans-serif',
  sansBold: '"SFCompactTextBold", "SFCompactText", sans-serif',
  sansSemibold: '"SFCompactTextSemiBold", "SFCompactText", sans-serif'
} as const;

export const radii = {
  sm: '6px',
  md: '10px',
  lg: '12px',
  xl: '14px',
  pill: '100px'
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '14px',
  lg: '24px',
  xl: '36px',
  xxl: '60px'
} as const;

/** Responsive breakpoints (min-width) */
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px'
} as const;

/** Media query helpers for styled-components */
export const media = {
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  xxl: `@media (min-width: ${breakpoints.xxl})`
} as const;

/**
 * Full theme object for styled-components ThemeProvider.
 */
export const v2Theme = {
  colors,
  shadows,
  fonts,
  radii,
  spacing,
  breakpoints,
  media
} as const;

export type V2Theme = typeof v2Theme;

/**
 * CSS custom properties string — injected into the .v2-root scope.
 */
export const cssVariables = `
  --terracotta: ${colors.terracotta};
  --terracotta-dark: ${colors.terracottaDark};
  --terracotta-soft: ${colors.terracottaSoft};
  --ink: ${colors.ink};
  --ink-muted: ${colors.inkMuted};
  --ink-faint: ${colors.inkFaint};
  --paper: ${colors.paper};
  --paper-warm: ${colors.paperWarm};
  --line: ${colors.line};
  --line-soft: ${colors.lineSoft};
  --olive: ${colors.olive};
  --olive-soft: ${colors.oliveSoft};
  --amber: ${colors.amber};
  --amber-soft: ${colors.amberSoft};
  --crimson: ${colors.crimson};
  --crimson-soft: ${colors.crimsonSoft};
  --indigo: ${colors.indigo};
  --indigo-soft: ${colors.indigoSoft};
  --white: ${colors.white};
  --shadow-sm: ${shadows.sm};
  --shadow-md: ${shadows.md};
  --shadow-lg: ${shadows.lg};
  --shadow-float: ${shadows.float};
  --font-serif: ${fonts.serif};
  --font-sans: ${fonts.sans};
  --font-mono: ${fonts.mono};
  --font-heading: ${fonts.heading};
  --font-heading-semibold: ${fonts.headingSemibold};
  --font-sans-bold: ${fonts.sansBold};
  --font-sans-semibold: ${fonts.sansSemibold};
  --radius-sm: ${radii.sm};
  --radius-md: ${radii.md};
  --radius-lg: ${radii.lg};
  --radius-xl: ${radii.xl};
  --radius-pill: ${radii.pill};
`;
